// =====================================================
// STRIPE CONNECT API + LANDLORD FUNDING SOURCE API + PAYMENT CONFIGURATION API +
// PROPERTY BANK ACCOUNT API + PENDING LATE FEE API + CHARGE CONFIGURATION API +
// LEDGER API + Analytics API + EXTERNAL PAYMENTS
// =====================================================

import { z } from 'zod';
import { apiFetch } from './api.core';
import {
  ConnectAccountStatusResponseSchema,
  PendingLateFeeResponseSchema,
  PaymentReadinessIssueSchema,
  PropertyBankInfoSchema,
  ChargeConfigResponseSchema,
  CreateChargeConfigRequestSchema,
  UpdateChargeConfigRequestSchema,
  LedgerEntryResponseSchema,
  CreateManualChargeRequestSchema,
  PendingCreditResponseSchema,
  PortfolioMetricsResponseSchema,
  PropertyMetricsResponseSchema,
  PastDueItemSchema,
  MatchingActivityEntryResponseSchema,
} from '@resida/api-types/payments';

// =====================================================
// STRIPE CONNECT API
// =====================================================

// Derived from ConnectAccountStatusResponseSchema in @resida/api-types/payments
export type StripeConnectStatus = z.infer<typeof ConnectAccountStatusResponseSchema>;

export interface StripeConnectOnboardResult {
  stripeAccountId: string;
  onboardingUrl: string;
  status: string;
}

/**
 * Start Stripe Connect onboarding
 */
export async function startStripeConnectOnboarding(): Promise<StripeConnectOnboardResult> {
  return apiFetch<StripeConnectOnboardResult>('/landlord/api/stripe-connect/onboard', {
    method: 'POST',
  });
}

/**
 * Get Stripe Connect account status
 */
export async function getStripeConnectStatus(): Promise<StripeConnectStatus> {
  return apiFetch<StripeConnectStatus>('/landlord/api/stripe-connect/status');
}

/**
 * Refresh Stripe Connect onboarding link
 */
export async function refreshStripeConnectLink(): Promise<{ onboardingUrl: string }> {
  return apiFetch<{ onboardingUrl: string }>('/landlord/api/stripe-connect/refresh-link', {
    method: 'POST',
  });
}

// =====================================================
// LANDLORD FUNDING SOURCE API
// =====================================================

export interface LandlordFundingSource {
  id: string;
  bankName: string;
  accountType: string;
  accountMask: string;
  friendlyName: string | null;
  status: 'pending' | 'verified' | 'unverified' | 'failed';
  verificationMethod: 'instant' | 'micro_deposit' | 'instant_micro_deposit' | null;
  setupIntentId: string | null;
  isPrimary: boolean;
  createdAt: string;
}

export interface SetupIntentResponse {
  clientSecret: string;
  stripeAccountId: string;
}

export interface CreateLandlordFundingSourceRequest {
  paymentMethodId: string;
  setupIntentId: string;
  friendlyName?: string;
}

/**
 * List all funding sources for the current landlord
 */
export async function listLandlordFundingSources(): Promise<{ fundingSources: LandlordFundingSource[] }> {
  return apiFetch<{ fundingSources: LandlordFundingSource[] }>('/landlord/api/funding-sources');
}

/**
 * Create a Stripe SetupIntent for bank account linking via Financial Connections.
 * Returns the clientSecret to pass to stripe.collectBankAccountForSetup().
 */
export async function createSetupIntent(): Promise<SetupIntentResponse> {
  return apiFetch<SetupIntentResponse>('/landlord/api/funding-sources/setup-intent', {
    method: 'POST',
  });
}

/**
 * Attach a confirmed bank account to this landlord's profile.
 * Call after stripe.confirmUsBankAccountSetup() succeeds with the payment method ID.
 */
export async function createLandlordFundingSource(
  request: CreateLandlordFundingSourceRequest
): Promise<LandlordFundingSource> {
  return apiFetch<LandlordFundingSource>('/landlord/api/funding-sources', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Update the friendly display name of a bank account
 */
export async function updateLandlordFundingSourceName(
  id: string,
  friendlyName: string | null
): Promise<LandlordFundingSource> {
  return apiFetch<LandlordFundingSource>(`/landlord/api/funding-sources/${id}/name`, {
    method: 'PATCH',
    body: JSON.stringify({ friendlyName }),
  });
}

/**
 * Set a funding source as primary
 */
export async function setLandlordFundingSourcePrimary(id: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`/landlord/api/funding-sources/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ isPrimary: true }),
  });
}

/**
 * Remove a landlord funding source
 */
export async function removeLandlordFundingSource(id: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`/landlord/api/funding-sources/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Sync landlord funding sources from Stripe
 */
export async function syncLandlordFundingSources(): Promise<{ fundingSources: LandlordFundingSource[] }> {
  return apiFetch<{ fundingSources: LandlordFundingSource[] }>('/landlord/api/funding-sources/sync', {
    method: 'POST',
  });
}

/**
 * Verify a pending landlord bank account using micro-deposit amounts or descriptor code
 */
export async function verifyLandlordMicroDeposits(
  fundingSourceId: string,
  request: { type: 'micro_deposit'; amounts: [number, number] } | { type: 'instant_micro_deposit'; descriptorCode: string }
): Promise<LandlordFundingSource> {
  return apiFetch<LandlordFundingSource>(
    `/landlord/api/funding-sources/${fundingSourceId}/verify-micro-deposits`,
    { method: 'POST', body: JSON.stringify(request) }
  );
}

// =====================================================
// PAYMENT CONFIGURATION API
// =====================================================

export type LateFeeType = 'flat' | 'percentage' | 'daily';


export interface PaymentConfig {
  id: string;
  leaseId: string;
  rentDueDay: number;
  gracePeriodDays: number;
  lateFeeType: LateFeeType | null;
  lateFeeAmount: number | null;
  lateFeeMaxAmount: number | null;
  destinationId: string | null;
  destination?: LandlordFundingSource;
  paymentMethod: 'none' | 'ach' | 'external';
  methodIntent?: 'ach' | 'external' | 'skipped' | null;
  termsSetAt: string | null;
  lateFeesSetAt: string | null;
  externalPhase: 'learning' | 'active' | null;
  externalPhaseSetAt: string | null;
  zelleRecipientInfo: string | null;
  autoApplyThreshold: number;
  reminderEnabled: boolean;
  reminderDaysBefore: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpdatePaymentConfigInput {
  rentDueDay?: number;
  gracePeriodDays?: number;
  lateFeeType?: LateFeeType | null;
  lateFeeAmount?: number | null;
  lateFeeMaxAmount?: number | null;
  destinationId?: string | null;
  paymentMethod?: 'none' | 'ach' | 'external';
  methodIntent?: 'ach' | 'external' | 'skipped' | null;
  zelleRecipientInfo?: string | null;
  autoApplyThreshold?: number;
  reminderEnabled?: boolean;
  reminderDaysBefore?: number;
}

// Derived from PendingLateFeeResponseSchema in @resida/api-types/payments
export type PendingLateFee = z.infer<typeof PendingLateFeeResponseSchema>;

// Derived from PaymentReadinessIssueSchema in @resida/api-types/payments
export type PaymentReadinessIssue = z.infer<typeof PaymentReadinessIssueSchema>;

export interface PaymentReadiness {
  ach: { ready: boolean; issues: PaymentReadinessIssue[] };
  external: { ready: boolean; issues: PaymentReadinessIssue[] };
}

// Derived from PropertyBankInfoSchema in @resida/api-types/payments
export type PropertyBankInfo = z.infer<typeof PropertyBankInfoSchema>;

// =====================================================
// PROPERTY BANK ACCOUNT API
// =====================================================

export interface PropertyBankAccount {
  id: string;
  propertyId: string;
  linkedBankAccountId: string;
  isActive: boolean;
}

export async function getPropertyBankAccount(propertyId: string): Promise<PropertyBankAccount | null> {
  return apiFetch<PropertyBankAccount | null>(`/landlord/api/manager/properties/${propertyId}/bank-account`);
}

export async function setPropertyBankAccount(
  propertyId: string,
  linkedBankAccountId: string
): Promise<PropertyBankAccount> {
  return apiFetch<PropertyBankAccount>(`/landlord/api/manager/properties/${propertyId}/bank-account`, {
    method: 'PUT',
    body: JSON.stringify({ linkedBankAccountId }),
  });
}

export async function deletePropertyBankAccount(propertyId: string): Promise<void> {
  await apiFetch<void>(`/landlord/api/manager/properties/${propertyId}/bank-account`, {
    method: 'DELETE',
  });
}

/**
 * Get payment configuration for a lease
 */
export async function getPaymentConfig(leaseId: string): Promise<{ paymentConfig: PaymentConfig | null; propertyBankInfo: PropertyBankInfo | null; readiness: PaymentReadiness }> {
  return apiFetch<{ paymentConfig: PaymentConfig | null; propertyBankInfo: PropertyBankInfo | null; readiness: PaymentReadiness }>(`/landlord/api/leases/${leaseId}/payment-config`);
}

/**
 * Create payment configuration for a lease
 */
export async function createPaymentConfig(leaseId: string, data: UpdatePaymentConfigInput = {}): Promise<PaymentConfig> {
  return apiFetch<PaymentConfig>(`/landlord/api/leases/${leaseId}/payment-config`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update payment configuration for a lease
 */
export async function updatePaymentConfig(leaseId: string, data: UpdatePaymentConfigInput): Promise<PaymentConfig> {
  return apiFetch<PaymentConfig>(`/landlord/api/leases/${leaseId}/payment-config`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Enable payments for a lease
 */
export async function enablePayments(leaseId: string): Promise<PaymentConfig> {
  return apiFetch<PaymentConfig>(`/landlord/api/leases/${leaseId}/payment-config/enable`, {
    method: 'POST',
  });
}

/**
 * Disable payments for a lease
 */
export async function disablePayments(leaseId: string): Promise<PaymentConfig> {
  return apiFetch<PaymentConfig>(`/landlord/api/leases/${leaseId}/payment-config/disable`, {
    method: 'POST',
  });
}

// =====================================================
// PENDING LATE FEE API
// =====================================================

/**
 * Fetch pending late fees for a specific lease
 */
export async function fetchPendingLateFees(leaseId: string): Promise<{ pendingLateFees: PendingLateFee[] }> {
  return apiFetch<{ pendingLateFees: PendingLateFee[] }>(`/landlord/api/leases/${leaseId}/pending-late-fees`);
}

/**
 * Fetch pending late fees across all leases for the manager
 */
export async function fetchAllPendingLateFees(): Promise<{ pendingLateFees: PendingLateFee[] }> {
  return apiFetch<{ pendingLateFees: PendingLateFee[] }>(`/landlord/api/pending-late-fees`);
}

/**
 * Approve a pending late fee
 */
export async function approvePendingLateFee(id: string): Promise<{ id: string; sequenceNumber: number; balanceAfter: number }> {
  return apiFetch<{ id: string; sequenceNumber: number; balanceAfter: number }>(
    `/landlord/api/pending-late-fees/${id}/approve`,
    { method: 'POST' }
  );
}

/**
 * Dismiss a pending late fee
 */
export async function dismissPendingLateFee(id: string, reason?: string): Promise<{ success: true }> {
  return apiFetch<{ success: true }>(
    `/landlord/api/pending-late-fees/${id}/dismiss`,
    { method: 'POST', body: JSON.stringify({ reason }) }
  );
}

// =====================================================
// CHARGE CONFIGURATION API
// =====================================================

export type ChargeType = 'rent' | 'pet_rent' | 'parking' | 'utilities' | 'storage' | 'other';

// Derived from ChargeConfigResponseSchema in @resida/api-types/payments
export type LeaseChargeConfig = z.infer<typeof ChargeConfigResponseSchema>;

// Derived from CreateChargeConfigRequestSchema in @resida/api-types/payments
export type CreateChargeConfigInput = z.infer<typeof CreateChargeConfigRequestSchema>;

// Derived from UpdateChargeConfigRequestSchema in @resida/api-types/payments
export type UpdateChargeConfigInput = z.infer<typeof UpdateChargeConfigRequestSchema>;

/**
 * List all charge configs for a lease
 */
export async function listChargeConfigs(leaseId: string): Promise<{ chargeConfigs: LeaseChargeConfig[] }> {
  return apiFetch<{ chargeConfigs: LeaseChargeConfig[] }>(`/landlord/api/leases/${leaseId}/charge-configs`);
}

/**
 * Create a charge config for a lease
 */
export async function createChargeConfig(leaseId: string, data: CreateChargeConfigInput): Promise<LeaseChargeConfig> {
  return apiFetch<LeaseChargeConfig>(`/landlord/api/leases/${leaseId}/charge-configs`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update a charge config
 */
export async function updateChargeConfig(leaseId: string, chargeConfigId: string, data: UpdateChargeConfigInput): Promise<LeaseChargeConfig> {
  return apiFetch<LeaseChargeConfig>(`/landlord/api/leases/${leaseId}/charge-configs/${chargeConfigId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a charge config
 */
export async function deleteChargeConfig(leaseId: string, chargeConfigId: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`/landlord/api/leases/${leaseId}/charge-configs/${chargeConfigId}`, {
    method: 'DELETE',
  });
}

// =====================================================
// LEDGER API
// =====================================================

export type LedgerEntryType = 'charge' | 'payment' | 'credit' | 'adjustment' | 'reversal';
export type PaymentType = 'ach' | 'card' | 'cash' | 'check';

// Derived from LedgerEntryResponseSchema in @resida/api-types/payments
export type LedgerEntry = z.infer<typeof LedgerEntryResponseSchema>;

// Derived from CreateManualChargeRequestSchema in @resida/api-types/payments
export type CreateManualChargeInput = z.infer<typeof CreateManualChargeRequestSchema>;

export interface CreateCreditInput {
  amount: number;
  description: string;
  effectiveDate?: string; // ISO 8601 datetime string
}

// Derived from PendingCreditResponseSchema in @resida/api-types/payments
export type PendingCredit = z.infer<typeof PendingCreditResponseSchema>;

export type CreateCreditResponse =
  | { type: 'immediate'; entry: { id: string; sequenceNumber: string; balanceAfter: number } }
  | { type: 'pending'; pendingCredit: PendingCredit };

/**
 * Get ledger entries for a lease
 */
export async function getLedgerEntries(leaseId: string): Promise<{ entries: LedgerEntry[]; currentBalance: number }> {
  return apiFetch<{ entries: LedgerEntry[]; currentBalance: number }>(`/landlord/api/leases/${leaseId}/ledger`);
}

/**
 * Get current balance for a lease
 */
export async function getLeaseBalance(leaseId: string): Promise<{ balance: number }> {
  return apiFetch<{ balance: number }>(`/landlord/api/leases/${leaseId}/balance`);
}

/**
 * Create a manual charge for a lease
 */
export async function createManualCharge(leaseId: string, data: CreateManualChargeInput): Promise<{ id: string; sequenceNumber: string; balanceAfter: number }> {
  return apiFetch<{ id: string; sequenceNumber: string; balanceAfter: number }>(`/landlord/api/leases/${leaseId}/charges`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Issue a credit for a lease (supports optional effective date)
 */
export async function createCredit(leaseId: string, data: CreateCreditInput): Promise<CreateCreditResponse> {
  return apiFetch<CreateCreditResponse>(`/landlord/api/leases/${leaseId}/credits`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * List pending credits for a lease
 */
export async function listPendingCredits(leaseId: string): Promise<{ pendingCredits: PendingCredit[] }> {
  return apiFetch<{ pendingCredits: PendingCredit[] }>(`/landlord/api/leases/${leaseId}/pending-credits`);
}

/**
 * Cancel a pending credit
 */
export async function cancelPendingCredit(pendingCreditId: string): Promise<void> {
  await apiFetch<void>(`/landlord/api/pending-credits/${pendingCreditId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Analytics API
// ============================================================================

export interface DateRange {
  startDate: string;
  endDate: string;
}

// Derived from PortfolioMetricsResponseSchema in @resida/api-types/payments
export type PortfolioMetrics = z.infer<typeof PortfolioMetricsResponseSchema>;

// Derived from PropertyMetricsResponseSchema in @resida/api-types/payments
export type PropertyMetrics = z.infer<typeof PropertyMetricsResponseSchema>;

export interface PaymentTrendData {
  month: string;
  totalCharges: number;
  totalCollected: number;
}

// Derived from PastDueItemSchema in @resida/api-types/payments
export type PastDueItem = z.infer<typeof PastDueItemSchema>;

/**
 * Get portfolio payment metrics
 */
export async function getPortfolioMetrics(
  portfolioId: string,
  dateRange: DateRange
): Promise<PortfolioMetrics> {
  const params = new URLSearchParams({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });
  return apiFetch<PortfolioMetrics>(
    `/landlord/api/analytics/portfolio/${portfolioId}?${params}`
  );
}

/**
 * Get property payment metrics
 */
export async function getPropertyMetrics(
  propertyId: string,
  dateRange: DateRange
): Promise<PropertyMetrics> {
  const params = new URLSearchParams({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });
  return apiFetch<PropertyMetrics>(
    `/landlord/api/analytics/property/${propertyId}?${params}`
  );
}

/**
 * Get payment trends for a portfolio
 */
export async function getPaymentTrends(
  portfolioId: string,
  months: number = 6
): Promise<{ trends: PaymentTrendData[] }> {
  return apiFetch<{ trends: PaymentTrendData[] }>(
    `/landlord/api/analytics/portfolio/${portfolioId}/trends?months=${months}`
  );
}

/**
 * Get past due report for a portfolio
 */
export async function getPastDueReport(
  portfolioId: string
): Promise<{ items: PastDueItem[] }> {
  return apiFetch<{ items: PastDueItem[] }>(
    `/landlord/api/analytics/portfolio/${portfolioId}/past-due`
  );
}

/**
 * Export transactions as CSV
 */
export async function exportTransactions(
  portfolioId: string,
  dateRange: DateRange
): Promise<{ csv: string }> {
  const params = new URLSearchParams({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });
  return apiFetch<{ csv: string }>(
    `/landlord/api/analytics/portfolio/${portfolioId}/export?${params}`
  );
}

// =====================================================
// EXTERNAL PAYMENTS — MATCHING ACTIVITY
// =====================================================

// Derived from MatchingActivityEntryResponseSchema in @resida/api-types/payments
export type MatchingActivityEntry = z.infer<typeof MatchingActivityEntryResponseSchema>;

export interface MatchingActivityResponse {
  activity: MatchingActivityEntry[];
  total: number;
}

/**
 * Fetch matching activity log for a lease
 */
export async function getLeaseMatchingActivity(
  leaseId: string,
  params?: { limit?: number; offset?: number }
): Promise<MatchingActivityResponse> {
  const searchParams = new URLSearchParams();
  if (params?.limit != null) searchParams.set('limit', String(params.limit));
  if (params?.offset != null) searchParams.set('offset', String(params.offset));
  const qs = searchParams.toString();
  const endpoint = `/landlord/api/manager/leases/${leaseId}/matching-activity${qs ? `?${qs}` : ''}`;
  return apiFetch<MatchingActivityResponse>(endpoint);
}

// =====================================================
// EXTERNAL PAYMENTS — DASHBOARD SUMMARY
// =====================================================

export interface ExternalPaymentLastSyncTime {
  linkedBankAccountId: string;
  institutionName: string;
  lastSyncedAt: string | null;
}

export interface ExternalPaymentSummary {
  pendingReviewCount: number;
  autoAppliedThisMonth: number;
  autoAppliedAmountThisMonth: number;
  lastSyncTimes: ExternalPaymentLastSyncTime[];
  connectionIssues: number;
  disconnectedAccountCount?: number;
}

/**
 * Fetch external payments dashboard summary
 */
export async function getExternalPaymentSummary(): Promise<ExternalPaymentSummary> {
  return apiFetch<ExternalPaymentSummary>('/landlord/api/manager/external-payments/summary');
}
